<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { drawRoadWatermark, loadRoadWatermark } from '../domain/roadWatermark.js';
import { runtimeLogger } from '../services/runtimeLogger.js';
import { drawCenteredRoadText, drawGlassBead, drawGlassDot, drawGlassRing, drawGlassSlash, drawRoadBoard, drawRoadGridLine, drawTieArc, ROAD_THEMES } from '../domain/roadVisual.js';
import { decodeBeadRoadCode } from '../domain/beadRoad.js';
import { latestChangedVisibleRoadIndex, resolveRoadModel, shouldFlashRoadResult } from '../domain/roadFlash.js';

const props = defineProps({
  analysis: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'main' },
  seed: { type: Number, default: 1 },
  tableKey: { type: [Number, String], required: true },
  flashSeq: { type: Number, default: 0 },
  t: { type: Function, required: true }
});
const canvas = ref(null);
let newestBlinkHidden = false;
let newestBlinkInterval = 0;
let blinkRoadIndexes = [-1, -1, -1, -1, -1];

function roadSources() {
  const analysis = props.analysis || {};
  return [analysis.MainLuZhu || '', analysis.MainDalu || '', ...(Array.isArray(analysis.MainXiaoLu) ? analysis.MainXiaoLu : [])];
}

function flashNewestRoadMarks() {
  window.clearInterval(newestBlinkInterval);
  let toggles = 0;
  newestBlinkHidden = false;
  newestBlinkInterval = window.setInterval(() => {
    toggles += 1;
    newestBlinkHidden = !newestBlinkHidden;
    draw();
    if (toggles >= 6) {
      window.clearInterval(newestBlinkInterval);
      newestBlinkInterval = 0;
      newestBlinkHidden = false;
      draw();
    }
  }, 240);
}

function roadModel() {
  return resolveRoadModel(props.analysis, props.mode);
}

function draw() {
  const node = canvas.value;
  if (!node) return;
  const ctx = node.getContext('2d');
  const width = 1892;
  const height = 325;
  const beadWidth = 810;
  const ink = 'rgba(56,46,30,.98)';
  node.width = width;
  node.height = height;
  drawRoadBoard(ctx, width, height);
  for (let y = 0; y <= 6; y += 1) {
    drawRoadGridLine(ctx, 0, y * 54, width, y * 54, true);
    if (y < 6) drawRoadGridLine(ctx, beadWidth, y * 54 + 27, width, y * 54 + 27);
  }
  for (let x = 0; x <= 35; x += 1) drawRoadGridLine(ctx, x * 54, 0, x * 54, height, true);
  for (let x = 0; x <= 40; x += 1) drawRoadGridLine(ctx, beadWidth + x * 27, 0, beadWidth + x * 27, height);
  drawRoadWatermark(ctx, width, height, { widthRatio: .36, opacity: .09 });

  const model = roadModel();
  const beads = String(model.LuZhu || '').split('@');
  const bigRoad = String(model.DaLu || '').split('@');
  const smallRoads = Array.isArray(model.XiaoLu) ? model.XiaoLu.map((value) => String(value || '').split('@')) : [];
  const latestBeadIndex = blinkRoadIndexes[0];
  const latestBigRoadIndex = blinkRoadIndexes[1];
  const latestSmallRoadIndexes = blinkRoadIndexes.slice(2);
  const fallbackColor = (col, row, salt) => ((col * 5 + row * 3 + props.seed * 7 + salt) % 13) < 7 ? 'red' : 'blue';
  const fallbackVisible = (col, row, salt, limit) => col < limit && ((col * 7 + row * 11 + props.seed * 13 + salt) % 23) < 10;

  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 15; col += 1) {
      const sourceIndex = col + row * 20 + 1;
      const value = String(beads[sourceIndex] || '');
      if (!value || value === '0') continue;
      if (newestBlinkHidden && sourceIndex === latestBeadIndex) continue;
      const bead = decodeBeadRoadCode(value);
      if (!bead) continue;
      const color = bead.winner === 'banker' ? 'red' : (bead.winner === 'player' ? 'blue' : 'green');
      const label = bead.winner === 'banker'
        ? props.t('labelCompact.banker', 'B')
        : (bead.winner === 'player' ? props.t('labelCompact.player', 'P') : props.t('labelCompact.tie', 'T'));
      const cx = col * 54 + 27;
      const cy = row * 54 + 28;
      drawGlassBead(ctx, cx, cy, 23.5, ROAD_THEMES[color], label);
      if (bead.bankerPair) drawGlassDot(ctx, cx - 18, cy - 18, 6.5, ROAD_THEMES.red);
      if (bead.playerPair) drawGlassDot(ctx, cx + 17.5, cy + 17.5, 6.5, ROAD_THEMES.blue);
    }
  }

  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 40; col += 1) {
      const sourceIndex = col + row * 40 + 1;
      const bigValue = String(bigRoad[sourceIndex] || '');
      if (((bigValue && bigValue !== '0') || (!bigRoad.length && fallbackVisible(col, row, 1, 27 + props.seed * 2))) && !(newestBlinkHidden && sourceIndex === latestBigRoadIndex)) {
        const code = bigValue && bigValue !== '0' ? bigValue.charAt(0) : (fallbackColor(col, row, 1) === 'red' ? 'A' : 'B');
        const color = ['A', 'C'].includes(code) ? 'red' : 'blue';
        const cx = beadWidth + 14 + col * 27;
        const cy = 15 + row * 27;
        drawGlassRing(ctx, cx, cy, 11, ROAD_THEMES[color], 2.2);
        const tie = Number.parseInt(bigValue.slice(1), 10) || 0;
        if (['C', 'D'].includes(code) || tie > 0) {
          drawTieArc(ctx, cx, cy, 11, 4);
          if (tie) {
            ctx.fillStyle = ink;
            ctx.font = '13px sans-serif';
            drawCenteredRoadText(ctx, String(tie), cx, cy);
          }
        }
      }

      const derived0 = String(smallRoads[0]?.[col + row * 40 + 1] || '');
      if (((derived0 && derived0 !== '0') || (!smallRoads[0]?.length && fallbackVisible(col, row, 5, 30))) && !(newestBlinkHidden && sourceIndex === latestSmallRoadIndexes[0])) {
        const color = derived0 && derived0 !== '0' ? (derived0.charAt(0) === 'A' ? 'red' : 'blue') : fallbackColor(col, row, 5);
        const cx = beadWidth + 8 + col * 13.5;
        const cy = 169 + row * 13.5;
        drawGlassRing(ctx, cx, cy, 5, ROAD_THEMES[color], 1.8);
      }
      const derived1 = String(smallRoads[1]?.[col + row * 40 + 1] || '');
      if (((derived1 && derived1 !== '0') || (!smallRoads[1]?.length && fallbackVisible(col, row, 9, 28))) && !(newestBlinkHidden && sourceIndex === latestSmallRoadIndexes[1])) {
        const color = derived1 && derived1 !== '0' ? (derived1.charAt(0) === 'C' ? 'red' : 'blue') : fallbackColor(col, row, 9);
        const cx = beadWidth + 8 + col * 13.5;
        const cy = 250 + row * 13.5;
        drawGlassDot(ctx, cx, cy, 5, ROAD_THEMES[color]);
      }
      const derived2 = String(smallRoads[2]?.[col + row * 40 + 1] || '');
      if (((derived2 && derived2 !== '0') || (!smallRoads[2]?.length && fallbackVisible(col, row, 13, 36))) && !(newestBlinkHidden && sourceIndex === latestSmallRoadIndexes[2])) {
        const color = derived2 && derived2 !== '0' ? (derived2.charAt(0) === 'E' ? 'red' : 'blue') : fallbackColor(col, row, 13);
        const x = beadWidth + 544 + col * 13.5;
        const y = 255 + row * 13;
        drawGlassSlash(ctx, x, y + 8, x + 7, y, ROAD_THEMES[color], 3);
      }
    }
  }
}

onMounted(() => {
  draw();
  loadRoadWatermark().then(draw).catch((error) => runtimeLogger.error('[wanli-road] game watermark failed', error));
});
onBeforeUnmount(() => window.clearInterval(newestBlinkInterval));
watch(() => [props.tableKey, props.flashSeq, ...roadSources()], (next, previous = []) => {
  const [nextTableKey, nextSeq, ...nextSources] = next;
  const [previousTableKey, previousSeq, ...previousSources] = previous;
  if (!shouldFlashRoadResult({ previousTableKey, nextTableKey, previousSeq, nextSeq })) {
    if (String(previousTableKey ?? '') !== String(nextTableKey ?? '')) {
      window.clearInterval(newestBlinkInterval);
      newestBlinkInterval = 0;
      newestBlinkHidden = false;
      blinkRoadIndexes = [-1, -1, -1, -1, -1];
      nextTick(draw);
    }
    return;
  }
  blinkRoadIndexes = nextSources.map((value, index) => latestChangedVisibleRoadIndex(previousSources[index], value, index === 0
    ? { sourceColumns: 20, visibleColumns: 15, visibleRows: 6 }
    : { sourceColumns: 40, visibleColumns: 40, visibleRows: 6 }));
  if (blinkRoadIndexes.some((index) => index >= 1)) flashNewestRoadMarks();
});
watch(() => [props.analysis, props.mode, props.seed, props.t('labelCompact.banker'), props.t('labelCompact.player'), props.t('labelCompact.tie')], () => nextTick(draw), { deep: true });
</script>

<template><canvas ref="canvas" class="game-road-full" width="1892" height="325" /></template>
